import { createContext, useContext, useEffect, useState } from 'react';

const THEME_KEY = 'h101-theme';

export type Theme = 'auto' | 'dark' | 'light';

/** Light, dark or following the device; remembered in this browser. */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'auto';
    } catch {
      return 'auto';
    }
  });
  useEffect(() => {
    if (theme === 'auto') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
  }, [theme]);
  const next = () => setTheme(t => (t === 'auto' ? 'dark' : t === 'dark' ? 'light' : 'auto'));
  return { theme, next };
}

interface ShellState {
  /** The current page's title (from PageHeader), for the breadcrumb and the phone header. */
  setTitle: (title: string | null) => void;
  theme: Theme;
  nextTheme: () => void;
}

export const ShellContext = createContext<ShellState>({ setTitle: () => {}, theme: 'auto', nextTheme: () => {} });
export const useShell = () => useContext(ShellContext);

/** Tells the shell this page's title while it is shown. */
export function usePageTitle(title: string) {
  const { setTitle } = useShell();
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}
